import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { matchAPI, profileAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import LoadingAnimation from "../components/LoadingAnimation";
import UserProfileModal from "../components/UserProfileModal";
import { initSocket, getSocket, setUserId } from "../services/socket";
import AuthBackground from "../components/AuthBackground";

function ChatList() {
  const { user, unreadByMatch, activeMatchId } = useAuth();
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileModalUserId, setProfileModalUserId] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const loadingRef = useRef(false);
  const lastUpdateRef = useRef(Date.now());
  const activeMatchIdRef = useRef(null);
  console.log(lastUpdate);

  const normalizeMatchId = useCallback((id) => {
    if (!id) return null;
    if (typeof id === "string") return id;
    if (typeof id === "object") {
      return id._id?.toString?.() || id.toString?.() || null;
    }
    return null;
  }, []);

  // Load chats and matches with profiles
  const loadChatsAndMatches = useCallback(async (forceRefresh = false) => {
    // Prevent too frequent updates (debounce to max once per second)
    const now = Date.now();
    if (!forceRefresh && now - lastUpdateRef.current < 1000) {
      console.log("[ChatList] Skipping update - too soon");
      return;
    }

    if (loadingRef.current) {
      console.log("[ChatList] Already loading, skipping...");
      return;
    }
    loadingRef.current = true;
    lastUpdateRef.current = now;

    try {
      // Only show loading spinner on initial/force refresh
      if (forceRefresh) {
        setLoading(true);
      }

      console.log("[ChatList] 📡 Fetching fresh chat list from API...");

      // Load chat list with unread counts
      const chatResponse = await matchAPI.getChatList();
      const chatData = chatResponse.data.data || [];

      console.log("[ChatList] 📊 Received chat data:", {
        chatCount: chatData.length,
        chatsWithUnread: chatData.filter((c) => c.totalUnreadCount > 0).length,
        firstChat: chatData[0]
          ? {
              matchId: chatData[0].matchId,
              totalUnread: chatData[0].totalUnreadCount,
              latestMessage: chatData[0].latestMessage?.content?.substring(
                0,
                30,
              ),
            }
          : null,
      });

      // Load all matches
      const matchResponse = await matchAPI.getMatches();
      const matchData = matchResponse.data.data || [];

      // Get profile data for each chat
      const chatsWithProfiles = await Promise.all(
        chatData.map(async (chat) => {
          try {
            const profileResponse = await profileAPI.getProfileByUserId(
              chat.otherUserId,
            );
            return {
              ...chat,
              profile: profileResponse.data.data,
            };
          } catch (err) {
            console.error("Error loading profile:", err);
            return { ...chat, profile: null };
          }
        }),
      );

      console.log(
        "[ChatList] 🎯 Setting chats in state, count:",
        chatsWithProfiles.length,
      );
      setChats(chatsWithProfiles);
      setMatches(matchData);
      setError("");
      setLastUpdate(new Date());
      console.log("[ChatList] ✅ Chat list updated successfully!");
    } catch (err) {
      console.error("[ChatList] ❌ Error loading chats:", err);
      setError("Failed to load chats");
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadChatsAndMatches(true);
  }, [loadChatsAndMatches]);

  useEffect(() => {
    activeMatchIdRef.current = activeMatchId ? activeMatchId.toString() : null;
  }, [activeMatchId]);

  useEffect(() => {
    if (!chats.length) return;

    setChats((prevChats) =>
      prevChats.map((chat) => {
        const chatId = normalizeMatchId(chat.matchId);
        if (!chatId) return chat;
        const nextUnread = unreadByMatch?.[chatId];
        if (!nextUnread) return chat;

        const unreadSpecialCount = nextUnread.unreadSpecialCount || 0;
        const unreadNormalCount = nextUnread.unreadNormalCount || 0;

        return {
          ...chat,
          unreadSpecialCount,
          unreadNormalCount,
          totalUnreadCount: unreadSpecialCount + unreadNormalCount,
          hasUnreadSpecial: nextUnread.hasUnreadSpecial || false,
          latestMessage: nextUnread.latestMessage || chat.latestMessage,
          lastMessageAt: nextUnread.lastMessageAt || chat.lastMessageAt,
        };
      }),
    );
  }, [unreadByMatch, normalizeMatchId, chats.length]);

  // Optimized real-time socket listener for instant message and unread updates
  useEffect(() => {
    if (!user?.userId) return;

    initSocket();
    setUserId(user.userId);
    const socket = getSocket();
    if (!socket) return;

    // Helper: update chat in state efficiently
    const updateChatInState = (matchId, message) => {
      const normalizedMatchId = normalizeMatchId(matchId);
      if (!normalizedMatchId) return;

      setChats((prevChats) => {
        let found = false;
        const updatedChats = prevChats.map((chat) => {
          const chatId = normalizeMatchId(chat.matchId);
          if (!chatId || chatId !== normalizedMatchId) return chat;
          // Ignore if message is from current user
          if (
            message.senderId &&
            user.userId &&
            message.senderId.toString() === user.userId.toString()
          ) {
            return chat;
          }
          // Prevent duplicate messages
          if (chat.latestMessage && chat.latestMessage._id === message._id) {
            return chat;
          }
          found = true;

          if (activeMatchIdRef.current === normalizedMatchId) {
            return {
              ...chat,
              unreadSpecialCount: 0,
              unreadNormalCount: 0,
              totalUnreadCount: 0,
              hasUnreadSpecial: false,
              latestMessage: message,
              lastMessageAt: message.createdAt || new Date().toISOString(),
            };
          }

          // Update unread count
          const unreadSpecialCount = message.isSpecial
            ? (chat.unreadSpecialCount || 0) + 1
            : chat.unreadSpecialCount || 0;
          const unreadNormalCount = !message.isSpecial
            ? (chat.unreadNormalCount || 0) + 1
            : chat.unreadNormalCount || 0;
          return {
            ...chat,
            unreadSpecialCount,
            unreadNormalCount,
            totalUnreadCount: unreadSpecialCount + unreadNormalCount,
            hasUnreadSpecial: unreadSpecialCount > 0,
            latestMessage: message,
            lastMessageAt: message.createdAt || new Date().toISOString(),
          };
        });
        // Move updated chat to top if found
        if (found) {
          const idx = updatedChats.findIndex((c) => c.matchId === matchId);
          const chatToTop = updatedChats[idx];
          const rest = updatedChats.filter((c, i) => i !== idx);
          // Efficient sorting: updated chat first, then by unread/special/lastMessage
          return [
            chatToTop,
            ...rest.sort((a, b) => {
              if (a.hasUnreadSpecial && !b.hasUnreadSpecial) return -1;
              if (!a.hasUnreadSpecial && b.hasUnreadSpecial) return 1;
              if (a.totalUnreadCount > 0 && b.totalUnreadCount === 0) return -1;
              if (a.totalUnreadCount === 0 && b.totalUnreadCount > 0) return 1;
              return new Date(b.lastMessageAt) - new Date(a.lastMessageAt);
            }),
          ];
        }
        // If not found, return original
        return updatedChats;
      });
    };

    // Handle instant message update on 'newMessage'
    const handleNewMessage = (payload) => {
      const message = payload?.message || payload;
      const matchId = payload?.matchId || message?.matchId;
      if (!matchId || !message) return;
      updateChatInState(matchId, message);
    };

    // Handle unread update on 'newMessageNotification' (for compatibility)
    const handleNewMessageNotification = (payload) => {
      const message = payload?.message || payload;
      const matchId = payload?.matchId || message?.matchId;
      if (!matchId || !message) return;
      updateChatInState(matchId, message);
    };

    socket.on("newMessage", handleNewMessage);
    socket.on("newMessageNotification", handleNewMessageNotification);

    // Cleanup
    return () => {
      socket.off("newMessage", handleNewMessage);
      socket.off("newMessageNotification", handleNewMessageNotification);
    };
  }, [user?.userId, normalizeMatchId]);

  const handleChatClick = (matchId) => {
    navigate(`/chat/${matchId}`);
  };

  const handleMatchClick = (matchId) => {
    navigate(`/chat/${matchId}`);
  };

  const handleProfileClick = (e, userId, fallbackProfile) => {
    e.stopPropagation();
    if (!userId) return;
    const normalizedId = userId?.toString?.() || userId;
    setSelectedProfile(fallbackProfile || null);
    setProfileModalUserId(normalizedId);
    setShowProfileModal(true);
  };

  const formatTime = (date) => {
    const now = new Date();
    const messageDate = new Date(date);
    const diffInMinutes = Math.floor((now - messageDate) / (1000 * 60));

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;

    return messageDate.toLocaleDateString();
  };

  const getLastMessagePreview = (chat) => {
    if (!chat.latestMessage || !chat.latestMessage.content)
      return "Start chatting";

    const content = chat.latestMessage.content;
    const isFromMe =
      chat.latestMessage.senderId && user.userId
        ? chat.latestMessage.senderId.toString() === user.userId.toString()
        : false;
    const prefix = isFromMe ? "You: " : "";

    return `${prefix}${content.length > 50 ? content.substring(0, 50) + "..." : content}`;
  };

  if (loading) {
    return (
      <AuthBackground>
        <LoadingAnimation isVisible={loading} />
        <Navbar />
      </AuthBackground>
    );
  }

  return (
    <AuthBackground>
      <Navbar />

      <div className="flex-1 container mx-auto px-4 pt-20 pb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-6">Chats</h1>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        {/* Recent Matches - Horizontal Scroll */}
        {matches.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Recent Matches
            </h2>
            <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
              {matches.slice(0, 10).map((match) => (
                <div
                  key={match.matchId}
                  onClick={() => handleMatchClick(match.matchId)}
                  className="flex-shrink-0 cursor-pointer group"
                >
                  <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-indigo-500 group-hover:border-indigo-600 transition-all">
                    <img
                      src={match.profile?.photos?.[0] || "/default-avatar.png"}
                      alt={match.profile?.name || "Match"}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <p className="text-xs text-center mt-2 text-gray-700 truncate w-20">
                    {match.profile?.name || "User"}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* All Chats - Vertical List */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {chats.length === 0 ? (
            <div className="p-12 text-center">
              <div className="text-gray-400 mb-4">
                <svg
                  className="w-16 h-16 mx-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                No messages yet
              </h3>
              <p className="text-gray-500 mb-6">
                Start swiping to find matches and begin conversations!
              </p>
              <button
                onClick={() => navigate("/discovery")}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-indigo-700 hover:to-purple-700 transition"
              >
                Start Swiping
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {chats.map((chat) => (
                <div
                  key={chat.matchId}
                  onClick={() => handleChatClick(chat.matchId)}
                  className="flex items-center gap-4 p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  {/* User Photo */}
                  <div
                    onClick={(e) =>
                      handleProfileClick(e, chat.otherUserId, chat.profile)
                    }
                    className="relative flex-shrink-0"
                  >
                    <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-gray-200 hover:border-indigo-500 transition-colors">
                      <img
                        src={chat.profile?.photos?.[0] || "/default-avatar.png"}
                        alt={chat.profile?.name || "User"}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {/* Unread Badge */}
                    {chat.totalUnreadCount > 0 && (
                      <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                        {chat.totalUnreadCount > 9
                          ? "9+"
                          : chat.totalUnreadCount}
                      </div>
                    )}
                  </div>

                  {/* Chat Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold text-gray-800 truncate">
                        {chat.profile?.name || "Unknown User"}
                      </h3>
                      <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                        {formatTime(chat.lastMessageAt)}
                      </span>
                    </div>
                    <p
                      className={`text-sm truncate ${
                        chat.totalUnreadCount > 0
                          ? "font-semibold text-gray-900"
                          : "text-gray-600"
                      }`}
                    >
                      {getLastMessagePreview(chat)}
                    </p>
                  </div>

                  {/* Special Message Indicator */}
                  {chat.hasUnreadSpecial && (
                    <div className="flex-shrink-0">
                      <div className="bg-gradient-to-r from-pink-500 to-purple-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                        ✨
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Debug Info Banner - Shows last update time */}
        {/* {lastUpdate && (
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
              <div className="flex items-center justify-between">
                <span>🔄 Last updated: {lastUpdate.toLocaleTimeString()}</span>
                <span>
                  📊 Chats with unread:{" "}
                  {chats.filter((c) => c.totalUnreadCount > 0).length} /{" "}
                  {chats.length}
                </span>
              </div>
            </div>
          )} */}
      </div>

      {/* Profile Modal */}
      <UserProfileModal
        userId={profileModalUserId}
        isOpen={showProfileModal}
        onClose={() => {
          setShowProfileModal(false);
          setSelectedProfile(null);
          setProfileModalUserId(null);
        }}
        prefetchedProfile={selectedProfile}
      />

      <Footer />
    </AuthBackground>
  );
}

export default ChatList;
